import type { PlanRates } from '@/types'

/**
 * Base matrix data — default plans shown on first load.
 */
export const BASE_MATRIX_DATA: PlanRates[] = [
  {
    id: 'genesis-ev-low',
    plan: 'Genesis EV Home Low',
    fixedCharge: 0.6,
    peakRate: 0.34,
    shoulderRate: 0.34,
    nightRate: 0.17,
    peakFit: 0.12,
    offPeakFit: 0.12,
    freeWindows: [],
  },
  {
    id: 'genesis-ev-std',
    plan: 'Genesis EV Home Standard',
    fixedCharge: 2.1,
    peakRate: 0.28,
    shoulderRate: 0.28,
    nightRate: 0.14,
    peakFit: 0.12,
    offPeakFit: 0.12,
    freeWindows: [],
  },
  {
    id: 'meridian-2026-ev',
    plan: 'Meridian 2026 EV',
    fixedCharge: 0.9,
    peakRate: 0.32,
    shoulderRate: 0.32,
    nightRate: 0.14,
    peakFit: 0.12,
    offPeakFit: 0.12,
    freeWindows: [],
  },
  {
    id: 'octopus-peaker-low',
    plan: 'Octopus Peaker Low',
    fixedCharge: 0.6,
    peakRate: 0.38,
    shoulderRate: 0.38,
    nightRate: 0.11,
    peakFit: 0.23,
    offPeakFit: 0.1,
    freeWindows: [],
  },
  {
    id: 'power-edge-std',
    plan: 'Power Edge Standard',
    fixedCharge: 2.0,
    peakRate: 0.26,
    shoulderRate: 0.26,
    nightRate: 0.12,
    peakFit: 0.17,
    offPeakFit: 0.17,
    freeWindows: [],
  },
]

/**
 * Full plan preset library — 19 curated NZ electricity plans.
 * Rates are approximate estimates for Hamilton/Waikato incl GST.
 */
export const PLAN_PRESETS: PlanRates[] = [
  {
    plan: 'Genesis EV Home Low',
    fixedCharge: 0.6,
    peakRate: 0.34,
    shoulderRate: 0.34,
    nightRate: 0.17,
    peakFit: 0.12,
    offPeakFit: 0.12,
    url: 'https://www.genesisenergy.co.nz/power/plans/ev-home',
    freeWindows: [],
  },
  {
    plan: 'Genesis EV Home Standard',
    fixedCharge: 2.1,
    peakRate: 0.28,
    shoulderRate: 0.28,
    nightRate: 0.14,
    peakFit: 0.12,
    offPeakFit: 0.12,
    url: 'https://www.genesisenergy.co.nz/power/plans/ev-home',
    freeWindows: [],
  },
  {
    plan: 'Genesis Basic',
    fixedCharge: 2.0,
    peakRate: 0.28,
    shoulderRate: 0.28,
    nightRate: 0.28,
    peakFit: 0.12,
    offPeakFit: 0.12,
    url: 'https://www.genesisenergy.co.nz/power',
    freeWindows: [],
  },
  {
    plan: 'Meridian EV Plan',
    fixedCharge: 0.9,
    peakRate: 0.32,
    shoulderRate: 0.32,
    nightRate: 0.14,
    peakFit: 0.12,
    offPeakFit: 0.12,
    url: 'https://www.meridianenergy.co.nz/your-home/plans/ev',
    freeWindows: [],
  },
  {
    plan: 'Meridian Standard',
    fixedCharge: 1.8,
    peakRate: 0.29,
    shoulderRate: 0.29,
    nightRate: 0.29,
    peakFit: 0.12,
    offPeakFit: 0.12,
    url: 'https://www.meridianenergy.co.nz/your-home/plans',
    freeWindows: [],
  },
  {
    plan: 'Contact Good Charge',
    fixedCharge: 1.5,
    peakRate: 0.33,
    shoulderRate: 0.33,
    nightRate: 0.16,
    peakFit: 0.1,
    offPeakFit: 0.1,
    url: 'https://www.contact.co.nz/residential/good-charge',
    freeWindows: [],
  },
  {
    plan: 'Contact Good Nights',
    fixedCharge: 1.8,
    peakRate: 0.32,
    shoulderRate: 0.32,
    nightRate: 0.32,
    peakFit: 0.1,
    offPeakFit: 0.1,
    freeWindows: [{ enabled: true, days: 'weekdays', startHour: 21, endHour: 0, discount: 100 }],
    url: 'https://www.contact.co.nz/residential/good-nights',
    note: 'Free 9pm–12am Mon–Fri',
  },
  {
    plan: 'Contact Good Weekends',
    fixedCharge: 2.0,
    peakRate: 0.32,
    shoulderRate: 0.32,
    nightRate: 0.32,
    peakFit: 0.08,
    offPeakFit: 0.08,
    freeWindows: [{ enabled: true, days: 'weekends', startHour: 9, endHour: 17, discount: 100 }],
    url: 'https://www.contact.co.nz/residential/good-weekends',
    note: 'Free 9am–5pm Sat+Sun',
  },
  {
    plan: 'Contact Standard',
    fixedCharge: 2.0,
    peakRate: 0.3,
    shoulderRate: 0.3,
    nightRate: 0.3,
    peakFit: 0.08,
    offPeakFit: 0.08,
    url: 'https://www.contact.co.nz/residential',
    freeWindows: [],
  },
  {
    plan: 'Mercury EV',
    fixedCharge: 1.0,
    peakRate: 0.34,
    shoulderRate: 0.34,
    nightRate: 0.15,
    peakFit: 0.1,
    offPeakFit: 0.1,
    url: 'https://www.mercury.co.nz/plans',
    freeWindows: [],
  },
  {
    plan: 'Mercury Standard',
    fixedCharge: 2.1,
    peakRate: 0.27,
    shoulderRate: 0.27,
    nightRate: 0.27,
    peakFit: 0.1,
    offPeakFit: 0.1,
    url: 'https://www.mercury.co.nz/plans',
    freeWindows: [],
  },
  {
    plan: 'Octopus Peaker',
    fixedCharge: 0.6,
    peakRate: 0.38,
    shoulderRate: 0.38,
    nightRate: 0.11,
    peakFit: 0.23,
    offPeakFit: 0.1,
    url: 'https://octopusenergy.nz/plans',
    note: 'Peak FiT 23c at 7-11am+5-9pm weekdays',
    freeWindows: [],
  },
  {
    plan: 'Octopus Flexi',
    fixedCharge: 1.5,
    peakRate: 0.3,
    shoulderRate: 0.3,
    nightRate: 0.14,
    peakFit: 0.1,
    offPeakFit: 0.1,
    url: 'https://octopusenergy.nz/plans',
    freeWindows: [],
  },
  {
    plan: 'Octopus Working on Sunshine',
    fixedCharge: 1.5,
    peakRate: 0.3,
    shoulderRate: 0.2,
    nightRate: 0.3,
    peakFit: 0.1,
    offPeakFit: 0.1,
    url: 'https://octopusenergy.nz/plans',
    note: 'Cheap shoulder 9am-5pm',
    freeWindows: [],
  },
  {
    plan: 'Electric Kiwi MoveMaster',
    fixedCharge: 1.3,
    peakRate: 0.38,
    shoulderRate: 0.22,
    nightRate: 0.13,
    peakFit: 0.23,
    offPeakFit: 0.115,
    freeWindows: [{ enabled: true, days: 'all', startHour: 13, endHour: 14, discount: 100 }],
    url: 'https://www.electrickiwi.co.nz/plans',
    note: 'Night=weighted avg (2h offpeak+8h half). Free hour set to 1-2pm — change to your chosen hour',
  },
  {
    plan: 'Electric Kiwi Stay Ahead',
    fixedCharge: 2.0,
    peakRate: 0.28,
    shoulderRate: 0.28,
    nightRate: 0.28,
    peakFit: 0.08,
    offPeakFit: 0.08,
    url: 'https://www.electrickiwi.co.nz/plans',
    freeWindows: [],
  },
  {
    plan: 'Flick Flat Plan',
    fixedCharge: 2.0,
    peakRate: 0.3,
    shoulderRate: 0.3,
    nightRate: 0.3,
    peakFit: 0.1,
    offPeakFit: 0.1,
    url: 'https://www.flickelectric.co.nz',
    freeWindows: [],
  },
  {
    plan: 'Flick Off Peak',
    fixedCharge: 1.5,
    peakRate: 0.34,
    shoulderRate: 0.34,
    nightRate: 0.14,
    peakFit: 0.1,
    offPeakFit: 0.1,
    url: 'https://www.flickelectric.co.nz',
    freeWindows: [],
  },
  {
    plan: 'Power Edge Solar',
    fixedCharge: 2.0,
    peakRate: 0.26,
    shoulderRate: 0.26,
    nightRate: 0.12,
    peakFit: 0.17,
    offPeakFit: 0.17,
    url: 'https://www.poweredge.nz',
    note: 'Up to 20c buy-back',
    freeWindows: [],
  },
  {
    plan: 'Ecotricity EcoSaver',
    fixedCharge: 2.04,
    peakRate: 0.56,
    shoulderRate: 0.3715,
    nightRate: 0.2113,
    peakFit: 0.1774,
    offPeakFit: 0.1374,
    url: 'https://ecotricity.co.nz',
    note: 'Negative cent export rates in source text mapped as positive credits',
    freeWindows: [],
  },
]
